import { useState, useEffect, useRef, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle, Eye, X, Loader2, Search, LayoutGrid, List, Sparkles, CheckCircle2, AlertCircle, RefreshCw, Languages } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import {
    addPageToFunnel,
    removePageFromFunnel,
    removePageFromFunnelByInternalId,
    updateFunnel,
    getTranslateStatusByFunnel,
    getTranslationSummary,
    retranslateFunnel,
    syncFunnelPagesFromCategories,
    type TranslationSummaryPage,
} from '@/services/api/funnel';
import { getMetaPages } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { LANGUAGES } from '@/pages/Meta/Connection/constants/config-options';
import type { FunnelDetailResponse, FunnelPageInfo } from '@/types/funnel';
import type { MetaPage } from '@/pages/Meta/Connection/types/meta.types';

type FilterType = 'pages' | 'category';
type TranslationStatus = 'idle' | 'translating' | 'done' | 'failed' | 'cancelled';

// ─── AddPagesPopover ──────────────────────────────────────────────────────────
function AddPagesPopover({
    funnelId,
    linkedPageIds,
    onAdded,
}: {
    funnelId: number;
    linkedPageIds: Set<string>;
    onAdded: () => void;
}) {
    const { toast } = useToast();
    const [open, setOpen] = useState(false);
    const [allPages, setAllPages] = useState<MetaPage[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [filterType, setFilterType] = useState<FilterType>('pages');
    const [activeCategories, setActiveCategories] = useState<Set<number>>(new Set());
    const [toggling, setToggling] = useState<number | null>(null);
    const [syncing, setSyncing] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        getMetaPages()
            .then(data => {
                setAllPages(data.filter(p => p.is_active));
            })
            .finally(() => setLoading(false));
    }, [open]);

    const normalize = (s: string) =>
        s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

    const categories = Array.from(
        new Map(
            allPages
                .filter(p => p.internal_category_id && p.internal_category_name)
                .map(p => [p.internal_category_id as number, p.internal_category_name as string])
        ).entries()
    ).map(([id, name]) => ({ id, name }));

    // Filtra por busca/categoria
    const filtered = allPages.filter(p => {
        if (filterType === 'category' && activeCategories.size > 0)
            return activeCategories.has(p.internal_category_id as number);
        if (filterType === 'pages' && search.trim()) {
            const t = normalize(search);
            return normalize(p.page_name).includes(t) || p.page_id.includes(search.trim());
        }
        return true;
    });

    // Ordena: selecionadas primeiro
    const sorted = [...filtered].sort((a, b) => {
        const aLinked = linkedPageIds.has(a.page_id) ? 0 : 1;
        const bLinked = linkedPageIds.has(b.page_id) ? 0 : 1;
        return aLinked - bLinked;
    });

    const handleSyncCategories = async () => {
        if (activeCategories.size === 0 || syncing) return;
        setSyncing(true);
        try {
            const result = await syncFunnelPagesFromCategories(
                funnelId,
                Array.from(activeCategories),
            );
            if (result.added > 0) {
                toast({
                    title: `${result.added} página${result.added > 1 ? 's' : ''} vinculada${result.added > 1 ? 's' : ''}`,
                    description: result.skipped_existing > 0
                        ? `${result.skipped_existing} já estava${result.skipped_existing > 1 ? 'm' : ''} vinculada${result.skipped_existing > 1 ? 's' : ''}.`
                        : undefined,
                });
            } else if (result.total_pages_in_categories === 0) {
                toast({
                    variant: 'destructive',
                    title: 'Nenhuma página encontrada',
                    description: 'As categorias selecionadas não têm páginas ativas.',
                });
            } else {
                toast({
                    title: 'Já está sincronizado',
                    description: `Todas as ${result.skipped_existing} página(s) das categorias já estão no funil.`,
                });
            }
            onAdded();
        } catch (e: any) {
            toast({
                variant: 'destructive',
                title: 'Falha no sync',
                description: e?.response?.data?.detail || 'Erro ao sincronizar.',
            });
        } finally {
            setSyncing(false);
        }
    };

    const handleTogglePage = async (page: MetaPage) => {
        setToggling(page.id);
        const isLinked = linkedPageIds.has(page.page_id);
        try {
            if (isLinked) {
                // page.id é chatmeta_pages.id (PK pages) — usa rota que
                // resolve o funnel_page_id sozinho no backend.
                await removePageFromFunnelByInternalId(funnelId, page.id);
                toast({ title: `"${page.page_name}" removida` });
            } else {
                await addPageToFunnel(funnelId, page.id);
                toast({ title: `"${page.page_name}" adicionada` });
            }
            onAdded();
        } catch (e: any) {
            toast({ variant: 'destructive', title: e.response?.data?.detail || 'Erro' });
        } finally {
            setToggling(null);
        }
    };

    return (
        <Popover open={open} onOpenChange={v => { setOpen(v); if (!v) { setSearch(''); setActiveCategories(new Set()); } }}>
            <PopoverTrigger asChild>
                <button className="flex items-center gap-0.5 h-7 px-1.5 text-gray-400 hover:text-gray-700 border border-gray-200 rounded hover:border-gray-400 transition-colors shrink-0 bg-white"
                    title="Filtrar / Adicionar páginas">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                    </svg>
                    <svg className="w-2.5 h-2.5 -ml-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0 shadow-lg" align="start" side="bottom">
                {/* Toggle pages / categoria */}
                <div className="flex border-b border-gray-100">
                    {(['pages', 'category'] as FilterType[]).map(type => (
                        <button
                            key={type}
                            onClick={() => setFilterType(type)}
                            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
                                filterType === type
                                    ? 'text-gray-900 border-b-2 border-gray-800'
                                    : 'text-gray-400 hover:text-gray-600'
                            }`}
                        >
                            {type === 'pages' ? <List className="w-3.5 h-3.5" /> : <LayoutGrid className="w-3.5 h-3.5" />}
                            {type === 'pages' ? 'Páginas' : 'Categoria'}
                        </button>
                    ))}
                </div>

                {filterType === 'pages' && (
                    <div className="p-3 border-b border-gray-100">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                            <input
                                autoFocus
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Buscar página..."
                                className="w-full h-8 pl-8 pr-3 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-300"
                            />
                        </div>
                    </div>
                )}

                {filterType === 'category' && (
                    <div className="p-3 border-b border-gray-100 space-y-2">
                        {categories.length === 0 ? (
                            <p className="text-xs text-gray-400">Nenhuma categoria cadastrada</p>
                        ) : (
                            <>
                                <SearchableMultiSelect
                                    options={categories.map(c => ({ value: String(c.id), label: c.name }))}
                                    values={Array.from(activeCategories).map(String)}
                                    onValuesChange={(vals) => setActiveCategories(new Set(vals.map(Number)))}
                                    placeholder="Selecionar categorias…"
                                    searchPlaceholder="Buscar categoria…"
                                    showTags={false}
                                    selectedLabel={n => `${n} categoria${n > 1 ? 's' : ''} selecionada${n > 1 ? 's' : ''}`}
                                />
                                <button
                                    type="button"
                                    onClick={handleSyncCategories}
                                    disabled={activeCategories.size === 0 || syncing}
                                    className={`w-full h-8 text-xs font-medium rounded-md border transition-colors flex items-center justify-center gap-1.5 ${
                                        activeCategories.size === 0 || syncing
                                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                            : 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                                    }`}
                                    title="Vincula ao funil todas as páginas ativas das categorias selecionadas. Páginas já vinculadas viram no-op."
                                >
                                    {syncing ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                        <RefreshCw className="w-3.5 h-3.5" />
                                    )}
                                    {syncing
                                        ? 'Sincronizando…'
                                        : `Sincronizar páginas${activeCategories.size > 0 ? ` (${activeCategories.size} cat)` : ''}`}
                                </button>
                            </>
                        )}
                    </div>
                )}

                <div className="max-h-64 overflow-y-auto">
                    {loading ? (
                        <div className="flex justify-center py-6">
                            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                        </div>
                    ) : sorted.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-6">Nenhuma página encontrada</p>
                    ) : (
                        sorted.map(page => {
                            const isLinked = linkedPageIds.has(page.page_id);
                            return (
                                <button
                                    key={page.id}
                                    onClick={() => handleTogglePage(page)}
                                    disabled={toggling === page.id}
                                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-gray-50 transition-colors text-left disabled:opacity-50 ${
                                        isLinked ? 'bg-blue-50/50' : ''
                                    }`}
                                >
                                    {/* Checkbox */}
                                    <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                                        isLinked ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                                    }`}>
                                        {isLinked && (
                                            <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>

                                    {toggling === page.id ? (
                                        <Loader2 className="w-7 h-7 animate-spin text-blue-500 shrink-0" />
                                    ) : (
                                        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${isLinked ? 'bg-blue-600' : 'bg-gray-700'}`}>
                                            <span className="text-white font-semibold text-[9px]">
                                                {page.page_name.substring(0, 2).toUpperCase()}
                                            </span>
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className={`text-xs font-medium truncate ${isLinked ? 'text-blue-700' : 'text-gray-800'}`}>
                                            {page.page_name}
                                        </p>
                                        {page.internal_category_name && (
                                            <p className="text-[10px] text-gray-400">{page.internal_category_name}</p>
                                        )}
                                        {page.meta_user_name && (
                                            <p className="text-[10px] text-gray-400">Conta: {page.meta_user_name}</p>
                                        )}
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

// ─── PageChip: chip da página com status de tradução + ação retraduzir ───────
type PageStatus = TranslationSummaryPage['status'] | 'unknown';

function statusMeta(status: PageStatus) {
    switch (status) {
        case 'completed':
            return { dotClass: 'bg-green-500', label: 'Traduzida', textClass: 'text-green-700' };
        case 'partial':
            return { dotClass: 'bg-amber-500', label: 'Parcial', textClass: 'text-amber-700' };
        case 'pending':
            return { dotClass: 'bg-gray-300', label: 'Não traduzida', textClass: 'text-gray-600' };
        default:
            return { dotClass: 'bg-gray-200', label: 'Sem status', textClass: 'text-gray-500' };
    }
}

function PageChip({
    page,
    summary,
    aiTranslate,
    isViewing,
    onRemove,
    onRetranslate,
    onViewTranslation,
    isRetranslating,
}: {
    page: FunnelPageInfo;
    summary?: TranslationSummaryPage;
    aiTranslate: boolean;
    isViewing: boolean;
    onRemove: () => void;
    onRetranslate: () => void;
    onViewTranslation: () => void;
    isRetranslating: boolean;
}) {
    const [open, setOpen] = useState(false);
    const status: PageStatus = summary?.status ?? 'unknown';
    const meta = statusMeta(status);
    const canView = aiTranslate && (summary?.translated_nodes ?? 0) > 0;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <span className={`inline-flex items-center gap-1 text-xs font-medium pl-2.5 pr-1.5 py-1 rounded whitespace-nowrap ${
                isViewing ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-300' : 'bg-gray-100 text-gray-700'
            }`}>
                <button
                    onClick={onRemove}
                    className="text-gray-400 hover:text-red-500 transition-colors mr-0.5"
                    title="Remover página"
                >
                    <X className="w-3 h-3" />
                </button>
                <PopoverTrigger asChild>
                    <button
                        className="flex items-center gap-1.5 hover:opacity-80"
                        title="Ver status de tradução"
                    >
                        {aiTranslate && (
                            <span
                                className={`inline-block w-1.5 h-1.5 rounded-full ${meta.dotClass}`}
                                title={meta.label}
                            />
                        )}
                        {page.page_name || page.page_id}
                        {isViewing && <Eye className="w-3 h-3 ml-0.5" />}
                    </button>
                </PopoverTrigger>
            </span>
            <PopoverContent className="w-72 p-3 shadow-lg" align="start" side="bottom">
                <div className="space-y-2.5">
                    <div>
                        <p className="text-sm font-semibold text-gray-900 truncate">
                            {page.page_name || page.page_id}
                        </p>
                        <p className="text-[10px] text-gray-500 mt-0.5">ID: {page.page_id}</p>
                    </div>

                    <div className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-gray-500">Idioma:</span>
                        <span className="font-medium text-gray-800 truncate">
                            {summary?.language || page.language || '—'}
                        </span>
                    </div>

                    {aiTranslate && (
                        <div className="flex items-center justify-between gap-2 text-xs">
                            <span className="text-gray-500">Tradução:</span>
                            <span className={`inline-flex items-center gap-1.5 font-medium ${meta.textClass}`}>
                                <span className={`inline-block w-1.5 h-1.5 rounded-full ${meta.dotClass}`} />
                                {meta.label}
                                {summary && (
                                    <span className="text-gray-400 font-normal">
                                        ({summary.translated_nodes}/{summary.total_nodes})
                                    </span>
                                )}
                            </span>
                        </div>
                    )}

                    {/* Lista de nodes pendentes — só aparece se houver pendências */}
                    {aiTranslate && summary && (summary.pending_nodes?.length ?? 0) > 0 && (
                        <div className="text-xs space-y-1 border border-gray-100 rounded-md p-2 bg-gray-50">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                                Pendentes ({summary.pending_nodes!.length})
                            </p>
                            <ul className="space-y-0.5 max-h-28 overflow-y-auto">
                                {summary.pending_nodes!.map(n => (
                                    <li key={n.id} className="flex items-center gap-1.5 text-gray-700 truncate">
                                        <span className="inline-block w-1 h-1 rounded-full bg-gray-300 shrink-0" />
                                        <span className="truncate">{n.name}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {canView && (
                        <Button
                            type="button"
                            variant={isViewing ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                                onViewTranslation();
                                setOpen(false);
                            }}
                            className={`w-full h-8 text-xs gap-1.5 ${
                                isViewing ? 'bg-amber-600 hover:bg-amber-700 text-white border-amber-600' : ''
                            }`}
                        >
                            <Languages className="w-3.5 h-3.5" />
                            {isViewing ? 'Visualizando — clique pra trocar' : 'Visualizar tradução'}
                        </Button>
                    )}

                    {aiTranslate && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isRetranslating}
                            onClick={() => {
                                onRetranslate();
                                setOpen(false);
                            }}
                            className="w-full h-8 text-xs gap-1.5"
                        >
                            {isRetranslating ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                                <RefreshCw className="w-3.5 h-3.5" />
                            )}
                            Retraduzir esta página
                        </Button>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

// ─── Toolbar principal ─────────────────────────────────────────────────────────
interface ToolbarProps {
    funnelName: string;
    onBack: () => void;
    onValidate: () => void;
    onPreview?: () => void;
    funnel?: FunnelDetailResponse;
    viewingPageId?: string | null;
    onViewPageTranslation?: (pageId: string | null) => void;
}

export default function Toolbar({
    funnelName,
    onBack,
    onValidate,
    onPreview,
    funnel,
    viewingPageId = null,
    onViewPageTranslation,
}: ToolbarProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [aiTranslate, setAiTranslate] = useState(!!funnel?.ai_translate);
    const [sourceLanguage, setSourceLanguage] = useState(funnel?.source_language || 'Português - Brasil');
    const [translationStatus, setTranslationStatus] = useState<TranslationStatus>('idle');
    const [translationProgress, setTranslationProgress] = useState({ done: 0, total: 0, skipped: 0 });
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const doneTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => { setAiTranslate(!!funnel?.ai_translate); }, [funnel?.ai_translate]);
    useEffect(() => { setSourceLanguage(funnel?.source_language || 'Português - Brasil'); }, [funnel?.source_language]);

    const invalidate = () => {
        if (funnel) queryClient.invalidateQueries({ queryKey: ['funnel-details', String(funnel.id)] });
    };

    const stopPolling = useCallback(() => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }, []);

    const pollTranslation = useCallback(() => {
        if (!funnel) return;
        stopPolling();

        const doCheck = async () => {
            try {
                const data = await getTranslateStatusByFunnel(funnel.id);
                const done = parseInt(data.pages_done || '0', 10);
                const total = parseInt(data.pages_to_translate || data.pages_total || '0', 10);
                const skipped = parseInt(data.pages_skipped || '0', 10);
                setTranslationProgress({ done, total, skipped });

                if (data.status === 'running' || data.status === 'queued') {
                    setTranslationStatus('translating');
                } else if (data.status === 'completed') {
                    stopPolling();
                    setTranslationStatus('done');
                    doneTimerRef.current = setTimeout(() => setTranslationStatus('idle'), 6000);
                    invalidate();
                } else if (data.status === 'failed') {
                    stopPolling();
                    setTranslationStatus('failed');
                    doneTimerRef.current = setTimeout(() => setTranslationStatus('idle'), 6000);
                } else if (data.status === 'cancelled') {
                    stopPolling();
                    setTranslationStatus('cancelled');
                    doneTimerRef.current = setTimeout(() => setTranslationStatus('idle'), 4000);
                } else {
                    setTranslationStatus('idle');
                    stopPolling();
                }
            } catch {
                // silently retry
            }
        };

        doCheck();
        pollRef.current = setInterval(doCheck, 3000);
    }, [funnel?.id, stopPolling]);

    // Ao montar, se ai_translate está ativo, verifica se há tradução em andamento
    useEffect(() => {
        if (funnel?.ai_translate) {
            pollTranslation();
        }
        return () => {
            stopPolling();
            if (doneTimerRef.current) clearTimeout(doneTimerRef.current);
        };
    }, [funnel?.id, funnel?.ai_translate]);

    const updateMutation = useMutation({
        mutationFn: (data: Parameters<typeof updateFunnel>[1]) => updateFunnel(funnel!.id, data),
        onSuccess: invalidate,
        onError: () => toast({ variant: 'destructive', title: 'Erro ao salvar configurações' }),
    });

    const removeMutation = useMutation({
        mutationFn: (fp: FunnelPageInfo) => removePageFromFunnel(funnel!.id, fp.id),
        onSuccess: invalidate,
        onError: () => toast({ variant: 'destructive', title: 'Erro ao remover página' }),
    });

    // Resumo de tradução por página — só faz polling enquanto estiver traduzindo
    const summaryQuery = useQuery({
        queryKey: ['funnel-translation-summary', funnel?.id],
        queryFn: () => getTranslationSummary(funnel!.id),
        enabled: !!funnel?.id && !!aiTranslate,
        refetchInterval: translationStatus === 'translating' ? 3000 : false,
    });

    const summaryMap: Record<string, TranslationSummaryPage> =
        (summaryQuery.data?.pages ?? []).reduce((acc, p) => {
            acc[p.page_id] = p;
            return acc;
        }, {} as Record<string, TranslationSummaryPage>);

    const [retranslatingPageId, setRetranslatingPageId] = useState<string | null>(null);
    const retranslateMutation = useMutation({
        mutationFn: (pageId?: string) => retranslateFunnel(funnel!.id, pageId),
        onMutate: (pageId) => setRetranslatingPageId(pageId ?? '__all__'),
        onSuccess: () => {
            toast({ title: 'Retradução enfileirada' });
            setTranslationStatus('translating');
            setTranslationProgress({ done: 0, total: 0, skipped: 0 });
            setTimeout(() => pollTranslation(), 1500);
        },
        onError: (e: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao retraduzir',
                description: e?.response?.data?.detail || 'Tente novamente em instantes.',
            });
        },
        onSettled: () => setRetranslatingPageId(null),
    });

    // Quando a tradução termina, refaz o summary uma vez
    useEffect(() => {
        if (translationStatus === 'done') {
            queryClient.invalidateQueries({ queryKey: ['funnel-translation-summary', funnel?.id] });
        }
    }, [translationStatus, funnel?.id, queryClient]);

    const handleToggle = (val: boolean) => {
        setAiTranslate(val);
        const payload: Parameters<typeof updateFunnel>[1] = { ai_translate: val };
        if (val && !funnel?.source_language) {
            payload.source_language = sourceLanguage || 'Português - Brasil';
        }
        updateMutation.mutate(payload, {
            onSuccess: () => {
                if (val && funnel) {
                    setTranslationStatus('translating');
                    setTranslationProgress({ done: 0, total: 0, skipped: 0 });
                    setTimeout(() => pollTranslation(), 2000);
                } else if (!val) {
                    setTranslationStatus('idle');
                    stopPolling();
                }
            }
        });
    };

    const handleSourceLang = (val: string) => {
        setSourceLanguage(val);
        updateMutation.mutate({ source_language: val || undefined });
    };

    const linkedPageIds = new Set(funnel?.pages.map(p => p.page_id) ?? []);

    return (
        <div className="bg-white border-b border-gray-200 flex-shrink-0">
            {/* Row 1: navegação + ações */}
            <div className="h-14 flex items-center justify-between px-6">
                {/* Left */}
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="sm" onClick={onBack} className="text-gray-600 hover:text-gray-900">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Voltar
                    </Button>
                    <div className="h-6 w-px bg-gray-300" />
                    <div>
                        <h1 className="text-base font-semibold text-gray-900">{funnelName}</h1>
                        <p className="text-xs text-gray-500">Editor de Fluxo</p>
                    </div>
                </div>

                {/* Right */}
                <div className="flex items-center gap-3">
                    <Badge variant="outline" className="gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        Autoguardando
                    </Badge>

                    {onPreview && (
                        <Button variant="outline" size="sm" onClick={onPreview} className="gap-2">
                            <Eye className="w-4 h-4" />
                            Preview
                        </Button>
                    )}

                    <Button variant="outline" size="sm" onClick={onValidate} className="gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Validar Funil
                    </Button>
                </div>
            </div>

            {/* Row 2: páginas + tradução (só quando temos dados do funil) */}
            {funnel && (
                <div className="min-h-[40px] border-t border-gray-100 flex items-center px-6 gap-4 py-1.5">

                    {/* ── PÁGINAS ── */}
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-500 shrink-0">
                            Páginas
                        </span>

                        <div className="flex items-center gap-1 min-w-0 flex-1">
                            {/* Filtro / Adicionar integrado */}
                            <AddPagesPopover funnelId={funnel.id} linkedPageIds={linkedPageIds} onAdded={invalidate} />

                            {/* Chips das páginas selecionadas (máximo 5 visíveis) */}
                            <div className="flex items-center gap-1 min-w-0">
                                {funnel.pages.slice(0, 5).map(page => (
                                    <PageChip
                                        key={page.id}
                                        page={page}
                                        summary={summaryMap[page.page_id]}
                                        aiTranslate={aiTranslate}
                                        isViewing={viewingPageId === page.page_id}
                                        onRemove={() => removeMutation.mutate(page)}
                                        onRetranslate={() => retranslateMutation.mutate(page.page_id)}
                                        onViewTranslation={() => {
                                            if (!onViewPageTranslation) return;
                                            // Toggle: se já está visualizando essa página, sai do modo
                                            onViewPageTranslation(
                                                viewingPageId === page.page_id ? null : page.page_id
                                            );
                                        }}
                                        isRetranslating={retranslatingPageId === page.page_id}
                                    />
                                ))}
                            </div>

                            {/* Limpar tudo */}
                            {funnel.pages.length > 1 && (
                                <button
                                    onClick={() => {
                                        if (!confirm(`Remover todas as ${funnel.pages.length} páginas?`)) return;
                                        funnel.pages.forEach(p => removeMutation.mutate(p));
                                    }}
                                    className="text-gray-300 hover:text-gray-500 transition-colors shrink-0 p-0.5"
                                    title="Remover todas"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>

                        {/* Contador */}
                        {funnel.pages.length > 0 && (
                            <span className="shrink-0 text-[11px] font-bold bg-blue-500 text-white px-2 py-0.5 rounded-full">
                                {funnel.pages.length}
                            </span>
                        )}
                        <span className="text-[11px] text-gray-400 shrink-0">pág.</span>
                    </div>

                    <div className="h-5 w-px bg-gray-200 shrink-0" />

                    {/* ── TRADUÇÃO I.A ── */}
                    <div className="flex items-center gap-2.5 shrink-0">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            Tradução I.A
                        </span>

                        {/* Toggle custom (sem depender de bg-primary) */}
                        <button
                            role="switch"
                            aria-checked={aiTranslate}
                            onClick={() => handleToggle(!aiTranslate)}
                            disabled={updateMutation.isPending}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
                                aiTranslate ? 'bg-cyan-500' : 'bg-gray-300'
                            }`}
                        >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform ${
                                aiTranslate ? 'translate-x-[18px]' : 'translate-x-[3px]'
                            }`} />
                        </button>

                        {/* Status tag */}
                        {aiTranslate && translationStatus === 'idle' && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-cyan-50 text-cyan-700 border border-cyan-200 px-2 py-0.5 rounded-full">
                                <span className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
                                Ativo
                            </span>
                        )}
                        {translationStatus === 'translating' && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                {translationProgress.total > 0
                                    ? `Traduzindo ${translationProgress.done}/${translationProgress.total}`
                                    : 'Traduzindo...'}
                                {translationProgress.skipped > 0 && (
                                    <span className="text-amber-500 ml-0.5">({translationProgress.skipped} ignoradas)</span>
                                )}
                            </span>
                        )}
                        {translationStatus === 'done' && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-full">
                                <CheckCircle2 className="w-3 h-3" />
                                Tradução concluída
                            </span>
                        )}
                        {translationStatus === 'failed' && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded-full">
                                <AlertCircle className="w-3 h-3" />
                                Falha na tradução
                            </span>
                        )}
                        {translationStatus === 'cancelled' && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-gray-50 text-gray-600 border border-gray-200 px-2 py-0.5 rounded-full">
                                <AlertCircle className="w-3 h-3" />
                                Tradução cancelada
                            </span>
                        )}

                        {/* Idioma de origem */}
                        {aiTranslate && (
                            <SearchableSelect
                                value={sourceLanguage}
                                onValueChange={handleSourceLang}
                                options={[{ value: '', label: 'Idioma de origem (auto)' }, ...LANGUAGES]}
                                searchPlaceholder="Buscar idioma..."
                                className="h-6 text-xs w-48 border-gray-200"
                            />
                        )}

                        {/* Retraduzir todas */}
                        {aiTranslate && funnel.pages.length > 0 && (
                            <button
                                type="button"
                                disabled={retranslatingPageId === '__all__' || translationStatus === 'translating'}
                                onClick={() => retranslateMutation.mutate(undefined)}
                                title="Retraduzir todas as páginas"
                                className="h-6 px-2 inline-flex items-center gap-1 text-[10px] font-medium text-gray-600 hover:text-gray-900 border border-gray-200 rounded hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {retranslatingPageId === '__all__' ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                    <RefreshCw className="w-3 h-3" />
                                )}
                                Retraduzir todas
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
